/* eslint-disable react/prop-types */
import React from "react";
import { Bubble } from "react-chartjs-2";
import CustomTooltip from "app/common/chart/CustomTooltipComponent";
import { Typography, useMediaQuery } from "@material-ui/core";
import { nFormatter } from "lib/utils";
import { useSelector } from "react-redux";
import { SellOut } from "resources/constants";
import zoom from "chartjs-plugin-zoom";
import Chart from "chart.js";

Chart.plugins.register(zoom);

function ElasticityBubbleMapChartComponent({ data }) {
  const refs = React.useRef();
  const [tooltipProps, setTooltipProps] = React.useState({});
  const metadataSlice = useSelector((state) => state.metadata);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const labelByTenant = configCatSlice?.features.isUsaTerminology
    ? SellOut
    : "Sell Out";

  const setPositionAndData = (payload) => {
    setTooltipProps({
      top: payload.top,
      left: payload.left,
      position: "fixed",
      tooltipModel: payload.tooltipModel,
      showTooltip: true,
    });
  };

  const hide = () => {
    setTooltipProps({
      ...tooltipProps,
      showTooltip: false,
    });
  };

  const chartOptions = {
    pan: {
      enabled: true,
      mode: "xy",
    },
    zoom: {
      enabled: true,
      mode: "xy",
      speed: 0.05,
    },
    scales: {
      xAxes: [
        {
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
          },
          ticks: {
            reverse: true,
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 12,
          },
        },
      ],
      yAxes: [
        {
          id: "elasticity",
          scaleLabel: {
            labelString: `Elasticity`,
            display: false,
          },
          gridLines: {
            drawOnChartArea: true,
            drawTicks: false,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 6,
            maxRotation: 0,
            minRotation: 0,
            callback: React.useCallback((value) => {
              return `${nFormatter(value)}`;
            }, []),
          },
          position: "left",
        },
      ],
    },
    layout: {
      padding: {
        left: 10,
        right: 15,
        top: 15,
        bottom: 15,
      },
    },
    legend: {
      display: false,
    },
    tooltips: {
      enabled: false,
      intersect: isSmallScreen,
      mode: "nearest",
      custom: React.useCallback((tooltipModel) => {
        if (tooltipModel.opacity === 0) {
          hide();
          return;
        }

        const chart = refs.current.chartInstance;
        const { canvas } = chart.ctx;

        if (canvas) {
          const position = canvas.getBoundingClientRect();

          // set position of tooltip
          const left =
            tooltipModel.caretX <= chart.width / 2
              ? position.left + tooltipModel.caretX + 40
              : position.left + tooltipModel.caretX - 220;
          const top =
            tooltipModel.caretY <= chart.height / 2
              ? position.top + tooltipModel.caretY - 20
              : position.top + tooltipModel.caretY - 60;

          setPositionAndData({ top, left, tooltipModel });
        }
      }, []),
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <>
      {tooltipProps.showTooltip ? (
        <CustomTooltip
          tooltipProps={tooltipProps}
          items={[
            {
              content: (
                <div>
                  <Typography variant="body2">Elasticity</Typography>
                  <Typography variant="caption">
                    {`${refs.current.chartInstance.data.datasets[
                      tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                    ].data[
                      tooltipProps.tooltipModel.dataPoints[0].index
                    ].avg_weight_by_pred_units_last_price_elasticity.absolute.toFixed(
                      2
                    )}`}
                  </Typography>
                </div>
              ),
              borderStyle: {
                borderColor: "#A51557",
              },
            },
            {
              content: (
                <div>
                  <Typography variant="body2">{labelByTenant}</Typography>
                  <Typography variant="caption">
                    {`${nFormatter(
                      refs.current.chartInstance.data.datasets[
                        tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                      ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                        .sum_pred_units_last_price.absolute
                    )} ${metadataSlice.all.units[0].unit} `}
                  </Typography>
                </div>
              ),
              borderStyle: {
                borderColor: "transparent",
              },
            },
            {
              content: (
                <div>
                  <Typography variant="body2">Margin</Typography>
                  <Typography variant="caption">
                    {`${metadataSlice.all.margin[0].unit} ${nFormatter(
                      refs.current.chartInstance.data.datasets[
                        tooltipProps.tooltipModel.dataPoints[0].datasetIndex
                      ].data[tooltipProps.tooltipModel.dataPoints[0].index]
                        .sum_pred_margin_last_price.absolute
                    )}`}
                  </Typography>
                </div>
              ),
              borderStyle: {
                borderColor: "transparent",
              },
            },
          ]}
          tooltipData={{
            title:
              refs.current.chartInstance.data.datasets[
                tooltipProps.tooltipModel.dataPoints[0].datasetIndex
              ].data[tooltipProps.tooltipModel.dataPoints[0].index].label,
          }}
        />
      ) : null}
      <Bubble ref={refs} data={data} options={chartOptions} />
    </>
  );
}

export default ElasticityBubbleMapChartComponent;
